import React, { useState, useEffect } from 'react';
import { aeAPI } from '../../utils/api';
import { toast } from 'react-toastify';

const TRIAGE_COLOR  = { 1: '#dc2626', 2: '#ea580c', 3: '#ca8a04', 4: '#16a34a', 5: '#2563eb' };
const TRIAGE_LABEL  = { 1: 'Immediate', 2: 'Very Urgent', 3: 'Urgent', 4: 'Standard', 5: 'Non-Urgent' };
const MLC_TYPES = ['RTA', 'Assault', 'Burns', 'Poisoning', 'Drowning', 'Fall', 'Gunshot', 'Other'];

export default function AEReports() {
    const [tab, setTab] = useState('census'); // census | mlc | mci
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [report, setReport] = useState(null);
    const [mlcList, setMlcList] = useState([]);
    const [loading, setLoading] = useState(false);

    // MCI form
    const [mciForm, setMciForm] = useState({ event_name: '', event_type: 'Natural Disaster' });
    const [mciActive, setMciActive] = useState(false);
    const [mciSaving, setMciSaving] = useState(false);

    const loadCensus = async () => {
        setLoading(true);
        try {
            const res = await aeAPI.getAEReport(date);
            setReport(res.data);
        } catch { toast.error('Failed to load report'); }
        finally { setLoading(false); }
    };

    const loadMLC = async () => {
        setLoading(true);
        try {
            const res = await aeAPI.getMLCRegister(date);
            setMlcList(res.data);
        } catch { toast.error('Failed to load MLC register'); }
        finally { setLoading(false); }
    };

    useEffect(() => {
        if (tab === 'census') loadCensus();
        if (tab === 'mlc') loadMLC();
    }, [tab, date]);

    const activateMCI = async () => {
        if (!mciForm.event_name.trim()) return toast.error('Event name required');
        setMciSaving(true);
        try {
            await aeAPI.activateMCI(mciForm);
            setMciActive(true);
            toast.error('🚨 MCI MODE ACTIVATED — All departments notified!', { autoClose: 10000 });
        } catch (err) { toast.error(err.response?.data?.message || 'MCI activation failed'); }
        finally { setMciSaving(false); }
    };

    const printMLC = (mlc) => {
        const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><style>
            body { font-family: Arial, sans-serif; padding: 30px; font-size: 13px; }
            h2 { text-align: center; color: #1e3a5f; border-bottom: 2px solid #000; padding-bottom: 8px; }
            .row { display: flex; justify-content: space-between; margin: 8px 0; border-bottom: 1px dashed #ccc; padding-bottom: 6px; }
            .label { font-weight: 700; color: #374151; }
            .value { color: #111; }
            .mlc-box { border: 3px solid #dc2626; border-radius: 8px; padding: 12px; margin: 16px 0; text-align: center; }
            .mlc-num { font-size: 22px; font-weight: 900; color: #dc2626; }
            .sig { margin-top: 40px; display: flex; justify-content: space-between; }
            .sig-line { border-top: 1px solid #000; width: 180px; text-align: center; padding-top: 4px; font-size: 11px; }
            @media print { @page { size: A4; margin: 15mm; } }
        </style></head><body>
        <h2>🏥 HIMS<br><span style="font-size:14px;">MEDICO-LEGAL CASE (MLC) INTIMATION FORM</span></h2>
        <div class="mlc-box">
            <div style="font-size:11px;font-weight:700;">MLC NUMBER</div>
            <div class="mlc-num">${mlc.mlc_number || '—'}</div>
        </div>
        <div class="row"><span class="label">Patient Name:</span><span class="value">${mlc.full_name || 'Unknown'}</span></div>
        <div class="row"><span class="label">MRN:</span><span class="value">${mlc.mrn || '—'}</span></div>
        <div class="row"><span class="label">Age / Gender:</span><span class="value">${mlc.age_years ? mlc.age_years + ' yrs' : '?'} / ${mlc.gender || '?'}</span></div>
        <div class="row"><span class="label">CNIC:</span><span class="value">${mlc.cnic || '—'}</span></div>
        <div class="row"><span class="label">Case Type:</span><span class="value">${mlc.mlc_type || '—'}</span></div>
        <div class="row"><span class="label">Arrival Time:</span><span class="value">${mlc.arrival_time ? new Date(mlc.arrival_time).toLocaleString('en-PK') : '—'}</span></div>
        <div class="row"><span class="label">Triage Level:</span><span class="value">Level ${mlc.triage_level} — ${mlc.triage_category}</span></div>
        <div class="row"><span class="label">Police Station:</span><span class="value">${mlc.police_station || '—'}</span></div>
        <div class="row"><span class="label">Police Report No.:</span><span class="value">${mlc.police_report_number || '—'}</span></div>
        <div class="row"><span class="label">Police Informed At:</span><span class="value">${mlc.mlc_informed_time ? new Date(mlc.mlc_informed_time).toLocaleString('en-PK') : '—'}</span></div>
        <div class="row"><span class="label">Registered By:</span><span class="value">${mlc.registered_by_name || '—'}</span></div>
        <div class="row"><span class="label">Disposition:</span><span class="value">${mlc.disposition || 'Active'}</span></div>
        <div class="sig">
            <div class="sig-line">Medical Officer</div>
            <div class="sig-line">Nurse In-Charge</div>
            <div class="sig-line">Police Officer</div>
        </div>
        <p style="font-size:10px;text-align:center;margin-top:20px;">Generated by HIMS — ${new Date().toLocaleString('en-PK')}</p>
        </body></html>`;
        const win = window.open('', '_blank', 'width=700,height=900');
        win.document.write(html);
        win.document.close();
        setTimeout(() => win.print(), 500);
    };

    const printCensus = () => {
        if (!report) return;
        const rows = report.visits.map(v => `
            <tr>
                <td>${v.ae_number}</td>
                <td>${v.full_name || 'Unknown'}</td>
                <td>${v.mrn || '—'}</td>
                <td>${v.age_years || '?'}/${v.gender?.charAt(0) || '?'}</td>
                <td>${v.arrival_time ? new Date(v.arrival_time).toLocaleTimeString('en-PK', { hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                <td style="color:${TRIAGE_COLOR[v.triage_level] || '#000'};font-weight:700;">L${v.triage_level} ${TRIAGE_LABEL[v.triage_level]}</td>
                <td>${v.arrival_mode || '—'}</td>
                <td>${v.is_mlc ? 'MLC ⚖' : '—'}</td>
                <td>${v.disposition || v.status}</td>
                <td>${v.doctor_name || '—'}</td>
            </tr>`).join('');

        const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><style>
            body { font-family: Arial, sans-serif; padding: 20px; font-size: 11px; }
            h2 { text-align: center; color: #1e3a5f; }
            table { width: 100%; border-collapse: collapse; margin-top: 16px; }
            th { background: #1e3a5f; color: white; padding: 7px 8px; text-align: left; font-size: 10px; }
            td { padding: 6px 8px; border-bottom: 1px solid #e2e8f0; }
            tr:nth-child(even) { background: #f8fafc; }
            .stat { display: inline-block; margin: 4px 10px; font-weight: 700; }
            @media print { @page { size: A4 landscape; margin: 10mm; } }
        </style></head><body>
        <h2>🏥 HIMS — A&E Daily Census Report<br><span style="font-size:12px;font-weight:400;">Date: ${report.date}</span></h2>
        <div style="margin:10px 0;padding:10px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:6px;">
            <span class="stat">Total: ${report.summary.total}</span>
            <span class="stat">Active: ${report.summary.active}</span>
            <span class="stat">Completed: ${report.summary.completed}</span>
            <span class="stat">MLC: ${report.summary.mlc}</span>
            <span class="stat">Admitted: ${report.summary.admitted}</span>
            <span class="stat">Discharged: ${report.summary.discharged}</span>
            <span class="stat">Death: ${report.summary.death}</span>
            ${report.summary.avg_door_to_doctor ? `<span class="stat">Avg Door-to-Doctor: ${report.summary.avg_door_to_doctor} min</span>` : ''}
        </div>
        <table><thead><tr>
            <th>AE No.</th><th>Patient</th><th>MRN</th><th>Age/Sex</th><th>Arrival</th>
            <th>Triage</th><th>Mode</th><th>MLC</th><th>Disposition</th><th>Doctor</th>
        </tr></thead><tbody>${rows}</tbody></table>
        <p style="font-size:10px;text-align:center;margin-top:16px;">Generated by HIMS — ${new Date().toLocaleString('en-PK')}</p>
        </body></html>`;
        const win = window.open('', '_blank', 'width=1100,height=900');
        win.document.write(html);
        win.document.close();
        setTimeout(() => win.print(), 500);
    };

    return (
        <div style={{ maxWidth: 1100 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                <span style={{ fontSize: 32 }}>🚨</span>
                <div>
                    <h2 style={{ fontSize: 20, fontWeight: 700, color: '#dc2626', margin: 0 }}>A&E Reports & MLC</h2>
                    <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>Daily census, MLC register, MCI management</p>
                </div>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 20, borderBottom: '2px solid #e2e8f0', paddingBottom: 0 }}>
                {[
                    { key: 'census', label: '📊 Daily Census' },
                    { key: 'mlc', label: '⚖️ MLC Register' },
                    { key: 'mci', label: '🚨 MCI Mode' },
                ].map(t => (
                    <button key={t.key} onClick={() => setTab(t.key)}
                        style={{ padding: '10px 20px', border: 'none', borderBottom: tab === t.key ? '3px solid #dc2626' : '3px solid transparent', background: 'transparent', cursor: 'pointer', fontSize: 13, fontWeight: tab === t.key ? 700 : 400, color: tab === t.key ? '#dc2626' : '#374151' }}>
                        {t.label}
                    </button>
                ))}
            </div>

            {/* Date picker (census + mlc) */}
            {tab !== 'mci' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                    <input type="date" value={date} onChange={e => setDate(e.target.value)}
                        style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13 }} />
                    <button onClick={tab === 'census' ? loadCensus : loadMLC}
                        style={{ padding: '8px 16px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 7, cursor: 'pointer', fontSize: 12 }}>
                        🔄 Load
                    </button>
                    {tab === 'census' && report && (
                        <button onClick={printCensus}
                            style={{ padding: '8px 16px', background: '#1e3a5f', color: 'white', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                            🖨️ Print Census Report
                        </button>
                    )}
                </div>
            )}

            {/* ── CENSUS TAB ── */}
            {tab === 'census' && (
                loading ? <div style={{ textAlign: 'center', padding: 60, color: '#64748b' }}>Loading...</div> :
                report ? (
                    <div>
                        {/* Summary cards */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 20 }}>
                            {[
                                { label: 'Total Patients', value: report.summary.total, color: '#1e3a5f', bg: '#eff6ff' },
                                { label: 'Active', value: report.summary.active, color: '#d97706', bg: '#fffbeb' },
                                { label: 'Completed', value: report.summary.completed, color: '#16a34a', bg: '#f0fdf4' },
                                { label: 'MLC Cases', value: report.summary.mlc, color: '#dc2626', bg: '#fef2f2' },
                                { label: 'Admitted', value: report.summary.admitted, color: '#7c3aed', bg: '#f5f3ff' },
                                { label: 'Discharged', value: report.summary.discharged, color: '#0369a1', bg: '#eff6ff' },
                                { label: 'Deaths', value: report.summary.death, color: '#374151', bg: '#f8fafc' },
                                { label: 'Avg Door-to-Doctor', value: report.summary.avg_door_to_doctor ? report.summary.avg_door_to_doctor + ' min' : '—', color: '#0f766e', bg: '#f0fdfa' },
                            ].map(s => (
                                <div key={s.label} style={{ background: s.bg, border: `1px solid ${s.color}20`, borderRadius: 8, padding: '12px 14px', textAlign: 'center' }}>
                                    <div style={{ fontSize: 22, fontWeight: 900, color: s.color }}>{s.value}</div>
                                    <div style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>{s.label}</div>
                                </div>
                            ))}
                        </div>

                        {/* Triage breakdown */}
                        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                            {[1, 2, 3, 4, 5].map(l => (
                                <div key={l} style={{ flex: 1, background: TRIAGE_COLOR[l] + '15', border: `1px solid ${TRIAGE_COLOR[l]}40`, borderRadius: 8, padding: '8px', textAlign: 'center' }}>
                                    <div style={{ fontSize: 18, fontWeight: 900, color: TRIAGE_COLOR[l] }}>{report.summary.by_triage[l] || 0}</div>
                                    <div style={{ fontSize: 9, color: '#64748b', fontWeight: 600 }}>L{l} {TRIAGE_LABEL[l]}</div>
                                </div>
                            ))}
                        </div>

                        {/* Visits table */}
                        {report.visits.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: 40, background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', color: '#64748b' }}>No visits on this date</div>
                        ) : (
                            <div style={{ background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ background: '#1e3a5f' }}>
                                            {['AE No.', 'Patient', 'Arrival', 'Triage', 'Mode', 'MLC', 'Doctor', 'Disposition'].map(h => (
                                                <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'white' }}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {report.visits.map(v => (
                                            <tr key={v.id} style={{ borderBottom: '1px solid #f1f5f9', background: v.is_mlc ? '#fef2f2' : 'white' }}>
                                                <td style={{ padding: '10px 12px', fontSize: 12, fontWeight: 700, color: '#0369a1' }}>{v.ae_number}</td>
                                                <td style={{ padding: '10px 12px' }}>
                                                    <div style={{ fontSize: 13, fontWeight: 600 }}>{v.full_name || 'Unknown'}</div>
                                                    <div style={{ fontSize: 11, color: '#64748b' }}>{v.mrn} | {v.age_years}y/{v.gender?.charAt(0)}</div>
                                                </td>
                                                <td style={{ padding: '10px 12px', fontSize: 12 }}>{v.arrival_time ? new Date(v.arrival_time).toLocaleTimeString('en-PK', { hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                                                <td style={{ padding: '10px 12px' }}>
                                                    <span style={{ background: TRIAGE_COLOR[v.triage_level] + '20', color: TRIAGE_COLOR[v.triage_level], padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700 }}>
                                                        L{v.triage_level} {TRIAGE_LABEL[v.triage_level]}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '10px 12px', fontSize: 12 }}>{v.arrival_mode || '—'}</td>
                                                <td style={{ padding: '10px 12px', fontSize: 12 }}>{v.is_mlc ? <span style={{ color: '#dc2626', fontWeight: 700 }}>⚖️ MLC</span> : '—'}</td>
                                                <td style={{ padding: '10px 12px', fontSize: 12 }}>{v.doctor_name || '—'}</td>
                                                <td style={{ padding: '10px 12px', fontSize: 12 }}>{v.disposition || <span style={{ color: '#d97706' }}>{v.status}</span>}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                ) : <div style={{ textAlign: 'center', padding: 60, color: '#64748b' }}>Select date and click Load</div>
            )}

            {/* ── MLC TAB ── */}
            {tab === 'mlc' && (
                loading ? <div style={{ textAlign: 'center', padding: 60, color: '#64748b' }}>Loading...</div> :
                mlcList.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 60, background: 'white', borderRadius: 10, border: '1px solid #e2e8f0' }}>
                        <div style={{ fontSize: 40, marginBottom: 12 }}>⚖️</div>
                        <p style={{ color: '#64748b' }}>No MLC cases on this date</p>
                    </div>
                ) : (
                    <div style={{ background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                        <div style={{ padding: '12px 16px', background: '#fef2f2', borderBottom: '1px solid #fca5a5', fontSize: 13, fontWeight: 700, color: '#dc2626' }}>
                            ⚖️ MLC Register — {date} — {mlcList.length} case(s)
                        </div>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ background: '#f8fafc' }}>
                                    {['MLC No.', 'Patient', 'Case Type', 'Arrival', 'Triage', 'Police Station', 'Report No.', 'Informed At', 'Action'].map(h => (
                                        <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#374151', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {mlcList.map(m => (
                                    <tr key={m.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                        <td style={{ padding: '10px 12px', fontSize: 12, fontWeight: 700, color: '#dc2626' }}>{m.mlc_number || '—'}</td>
                                        <td style={{ padding: '10px 12px' }}>
                                            <div style={{ fontSize: 13, fontWeight: 600 }}>{m.full_name || 'Unknown'}</div>
                                            <div style={{ fontSize: 11, color: '#64748b' }}>{m.mrn} | {m.age_years}y/{m.gender?.charAt(0)}</div>
                                        </td>
                                        <td style={{ padding: '10px 12px', fontSize: 12, fontWeight: 700 }}>{m.mlc_type || '—'}</td>
                                        <td style={{ padding: '10px 12px', fontSize: 12 }}>{m.arrival_time ? new Date(m.arrival_time).toLocaleTimeString('en-PK', { hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                                        <td style={{ padding: '10px 12px' }}>
                                            <span style={{ background: TRIAGE_COLOR[m.triage_level] + '20', color: TRIAGE_COLOR[m.triage_level], padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700 }}>
                                                L{m.triage_level}
                                            </span>
                                        </td>
                                        <td style={{ padding: '10px 12px', fontSize: 12 }}>{m.police_station || '—'}</td>
                                        <td style={{ padding: '10px 12px', fontSize: 12 }}>{m.police_report_number || '—'}</td>
                                        <td style={{ padding: '10px 12px', fontSize: 12 }}>{m.mlc_informed_time ? new Date(m.mlc_informed_time).toLocaleTimeString('en-PK', { hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                                        <td style={{ padding: '10px 12px' }}>
                                            <button onClick={() => printMLC(m)}
                                                style={{ padding: '5px 12px', background: '#dc2626', color: 'white', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                                                🖨️ Print
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )
            )}

            {/* ── MCI TAB ── */}
            {tab === 'mci' && (
                <div style={{ maxWidth: 600 }}>
                    {mciActive && (
                        <div style={{ background: '#dc2626', color: 'white', borderRadius: 10, padding: '16px 20px', marginBottom: 20, textAlign: 'center' }}>
                            <div style={{ fontSize: 28, fontWeight: 900, letterSpacing: 2 }}>🚨 MCI MODE ACTIVE 🚨</div>
                            <div style={{ fontSize: 13, marginTop: 4 }}>All departments have been notified. Follow MCI protocol.</div>
                        </div>
                    )}

                    <div style={{ background: 'white', border: '2px solid #dc2626', borderRadius: 10, padding: 24, marginBottom: 20 }}>
                        <h3 style={{ fontSize: 15, fontWeight: 700, color: '#dc2626', marginBottom: 16 }}>🚨 Activate MCI Mode</h3>
                        <div style={{ marginBottom: 14 }}>
                            <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6 }}>Incident Name *</label>
                            <input value={mciForm.event_name} onChange={e => setMciForm(f => ({ ...f, event_name: e.target.value }))}
                                placeholder="e.g. Road Traffic Accident — GT Road, Bus Collision"
                                style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, boxSizing: 'border-box' }} />
                        </div>
                        <div style={{ marginBottom: 20 }}>
                            <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6 }}>Incident Type</label>
                            <select value={mciForm.event_type} onChange={e => setMciForm(f => ({ ...f, event_type: e.target.value }))}
                                style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13 }}>
                                {['RTA / Accident', 'Natural Disaster', 'Fire / Explosion', 'Building Collapse', 'Industrial Accident', 'Mass Poisoning', 'Terrorist Incident', 'Other'].map(t => (
                                    <option key={t} value={t}>{t}</option>
                                ))}
                            </select>
                        </div>
                        <button onClick={activateMCI} disabled={mciSaving}
                            style={{ width: '100%', padding: '14px', background: mciSaving ? '#9ca3af' : '#dc2626', color: 'white', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 900, cursor: 'pointer', letterSpacing: 1 }}>
                            {mciSaving ? 'Activating...' : '🚨 ACTIVATE MCI MODE'}
                        </button>
                    </div>

                    <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: 16 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#92400e', marginBottom: 8 }}>📋 MCI Protocol Checklist</div>
                        {[
                            'Notify Medical Superintendent / CMO',
                            'Call off-duty staff — activate on-call list',
                            'Set up triage area at hospital entrance',
                            'Assign colored triage tags (Red / Yellow / Green / Black)',
                            'Clear non-emergency patients from A&E',
                            'Activate Blood Bank for emergency stock',
                            'Notify OT — prepare emergency theatres',
                            'Contact nearest hospitals for overflow',
                            'Designate family liaison / media point',
                        ].map((item, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                <span style={{ fontSize: 16 }}>☐</span>
                                <span style={{ fontSize: 12, color: '#374151' }}>{item}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
